import { CerFormAdapter } from '@form/cer-form-adapter';

import { CerFormViewSet } from '@form/cer-form-view-set';
import { CerFormView } from '@form/cer-form-view';
import { FieldEditChangedArgs, FieldMetaDefaults, FieldMetadata, FieldMetaForeignKey, FieldWidthEnum, FieldMetadataUtil } from 'src/cer/cer-data/cer-data-types';
import { VoucherMessageViewDetailsVm, VoucherPostingTypeEnum } from 'src/app/api';
import { UiActionTypeEnum, UiCommandEvent, UiCommandSourceEnum } from '@ui/cer-ui-types';
import { PartyForm } from '@app/party/party.form';
import { PromptButton, clipboardWriteText } from '@ui/cer-ui-prompt.service';
import { CerUiIcons } from '@cer/cer-ui/cer-ui-icons';

const partyNavigation = 'partyNavigation';
const partyName = partyNavigation+'.name';

export class VendorForm extends CerFormAdapter {

  public override viewSetInit(viewSet: CerFormViewSet) {
    viewSet.viewAdd(this.viewGet());
  }

  private viewGet(): CerFormView {


    const callerDataApiName: string = this.routeOptions.parentView?.viewMetadata?.dataApiName;
    const dialogEditInPane: boolean = ['VoucherView', 'VoucherMessageView'].includes(callerDataApiName);

    const view = new CerFormView();

    view.onCommandEvent = (e => this.onCommand(e));

    view.viewMetadata = {
      name: 'kreditorer', dataApiName: 'Vendor',
      primaryKey: 'num', titleFields: ['num', 'name'], baseFields: ['id', 'history'],
      navigations: [
        { link: ['fk', 'navMenu'], title: 'Kreditor', foreignKey: { parentEntity: 'VendorView', parentColumn: 'num', column: 'num' } },
        { link: 'navMenu', foreignKey: { parentEntity: 'VoucherPostingVendor', parentColumn: 'accountNum', column: 'num' } },
        { link: 'navMenu', foreignKey: { parentEntity: 'VendorTrans', parentColumn: 'num', column: 'num' } }
      ]
    };
    view.listEditSettings = { mode: 'dialog', dialog: { editInPane: dialogEditInPane, height: '100%', width: '100%' } };
    view.fieldMetadata = [
      { fieldType: 'vendor', name: 'num', allowEdit: false, visibleAdd: false, orderBy: 'Ascending', orderByPriority: 1 },
      { name: 'name', text: 'Navn', tooltipText: 'Navn på part', widthType: FieldWidthEnum.L, required: true, onEditChanged: (e => this.onNameChanged(e)) },
      { fieldType: 'currency', required: true },
      { fieldType: 'vendorGroup', name: 'group', defaultValue: 'Kreditor', required: true },
      { fieldType: 'vendorTaxGroup' },
      { name: 'approved', text: 'Godkendt', format: 'CheckBox', visible: false, visibleAdd: false, visibleEdit: false },
      { name: 'blocked', text: 'Spærret', format: 'CheckBox' },
      { fieldType: 'paymentTerm' },
      { name: 'bankAccount', text: 'Bankkonto', tooltipText: 'Lokalt kontonr.', widthType: FieldWidthEnum.M, visible: true },
      { name: 'bankIban', text: 'IBAN', tooltipText: 'IBAN kontonr.', widthType: FieldWidthEnum.M, visible: false },
      { name: 'bankSwift', text: 'SWIFT', widthType: FieldWidthEnum.M, visible: false },
      { name: 'bankName', text: 'Bank navn', widthType: FieldWidthEnum.M, visible: false },
      { name: 'bankAddress', text: 'Bank adresse', widthType: FieldWidthEnum.M, visible: false },
      { fieldType: 'party', name: 'party', text: 'Kontakt', required: false, visible: true, allowEdit: true, allowEditOnAdd: true, foreignKey: { dataOptions: { maintainFromRowNavigation: true } }, onEditChanged: PartyForm.partyNumFKChanged },
    ];
    view.fieldMetadata.forEach(f =>
      FieldMetaForeignKey.init(view.viewMetadata, FieldMetaDefaults.init(f, true), true));

    // Add party columns through .partyNavigation
    const partyFieldMetadata = PartyForm.partyFieldMetaDataAsNavigation(partyNavigation);
    FieldMetadataUtil.mergeByName(partyName,partyFieldMetadata,{ required: false });
    view.fieldMetadata = view.fieldMetadata.concat(partyFieldMetadata);

    view.fieldMetadata.filter(f => f.visibleAdd == null).forEach(f => f.visibleAdd = true);
    view.fieldMetadata.filter(f => f.visibleEdit == null).forEach(f => f.visibleEdit = true);

    view.fieldGroupMetadata = [
      { idx: 1, name: 'identification', text: 'Identifikation', fields: ['id', 'num', 'name'] },
      { idx: 1, name: 'setup', text: 'Opsætning', fields: ['currency', 'group', 'taxGroup', 'approved', 'blocked'] },
      { idx: 2, name: 'payment', text: 'Betaling', fields: ['paymentTerm', 'bankAccount', 'bankIban', 'bankSwift', 'bankName', 'bankAddress'] },
      { idx: 3, name: 'party', text: 'Kontakt', fields: ['party', 'n.id', 'n.num', 'n.name', 'n.attention', 'n.tags', 'n.comments'] },
      { idx: 3, name: 'partyAddress', text: 'Kontakt adresse', fields: ['n.address', 'n.address2', 'n.address3', 'n.country'] },
      { idx: 4, name: 'partySetup', text: 'Kontakt opsætning', fields: ['n.publicId', 'n.publicIdIsCompany', 'n.legacyId'] },
      { idx: 4, name: 'communication', text: 'Kontakt info', fields: ['n.phone', 'n.email', 'n.url', 'n.language'] },
      { idx: 4, name: 'other', text: 'Øvrige', fields: [] }
    ];
    view.fieldGroupMetadata.forEach(g => g.fields = g.fields.map(f => f.startsWith('n.') ? f.replace('n.', 'partyNavigation.') : f));

    //this.handleCallerData(view, this.routeOptions.callerData);

    return view;
  }

  private onCommand(e: UiCommandEvent): void {
    PartyForm.onCommandEventFilterChoiceRequest(e);
    switch (e.source) {
      case UiCommandSourceEnum.ActionBegin:
        switch (e.actionType) {
          case UiActionTypeEnum.Save:

            const _this = this;
            const crudDataResolver: any = {}
            const crudDataPromise = new Promise(function (resolve, reject) { crudDataResolver.resolve = resolve; crudDataResolver.reject = reject; });
            crudDataPromise.then(evt => { _this.onCrudEvent(evt) });
            (<any>e.appComponent).grid.dataSource.crudDataResolver = crudDataResolver;

            if (e.subAction == 'add') {
              if (!this.validateSaveAddRow(e.rowData)) {
                e.cancel = true;
              };
            }
            break;
        }
        break;
    }
  }

  public onCrudEvent(e: any): any {
    if (e.isInsert) {
      const dto: any = e.row;
      const num: string = dto?.num;
      if (num != null) {
        const ui = this.context.ui;
        const content = `Tildelt konto: '${num}'`;
        const title = `Kreditor '${dto.name}' er oprettet.`;
        const copyBtn: PromptButton = {
          text: `Kopier kontonr. '${num}'`,
          iconCss: CerUiIcons.copy,
          action: 'clickOnly',
          click: (e) => { clipboardWriteText(num) }
        }
        ui.info(content, title, { buttons: [copyBtn] });
      }
    }
  }

  private onNameChanged(e: FieldEditChangedArgs): void {
    if (e.previousValue == e.editService.fieldValueGet(partyName)) {
      e.editService.fieldValueSet(partyName, e.value);
    }
  }

  private handleCallerData(view: CerFormView, callerData: any) {
    if (callerData.row != null) {
      switch (callerData.entityName) {
        case VoucherMessageViewDetailsVm.name:
          this.filterFromVoucherMessageViewDetailsVm(view, callerData.row);
          break;
      }
    }
  }

  private filterFromVoucherMessageViewDetailsVm(view: CerFormView, vm: VoucherMessageViewDetailsVm) {
    var values: string[] = [];

    // Recognized data
    vm.formFieldValues?.Vendor?.forEach(num => values.push(num));

    // Existing posting
    vm.voucherPostingViewDtoList?.filter(p => p.postingType == VoucherPostingTypeEnum.Vendor && p.accountNum?.length > 0)
      .map(p => p.accountNum)
      .forEach(num => values.push(num));

    // Add filter
    var values = [...new Set(values)];
    if (values.length > 0) {
      var f: FieldMetadata = view.fieldMetadata.find(f => f.name == 'num');
      if (f != null) {

        f.filterOnClient = true;
        if (values.length > 1) {
          f.filterOperator = 'in';
          f.filterValue = values;
        }
        else {
          f.filterOperator = 'equal';
          f.filterValue = values[0];
        }
      }
    }
  }


  public validateSaveAddRow(row: any): boolean {
    if (!(row.taxId?.length > 0)) {
      this.context.ui.warningToast("Momsnr. ikke angivet");
    }
    return true;
  }
}

/*
    if (vendorRe)
    if (f && emailValue?.length > 0) {
      
    }
*/
/*
var f = view.fieldMetadata.find(f => f.name == 'num');
var numValue = voucher.balanceAccountNum;
if (f && numValue?.length > 0) {
  f.filterOnClient = true;
  f.filterOperator = 'startswith';
  f.filterValue = numValue;
}
  */

